let pendingConfirmSource = null

document.addEventListener("click", (event) => {
  pendingConfirmSource = event.target.closest("[data-turbo-confirm], [data-turbo-method]")
}, true)

document.addEventListener("submit", (event) => {
  const submitter = event.submitter?.closest?.("[data-turbo-confirm], [data-turbo-method]")
  pendingConfirmSource = submitter || pendingConfirmSource
}, true)

export function installTurboConfirm(Turbo) {
  Turbo.config.forms.confirm = (message, formElement, submitter) =>
    new Promise((resolve) => {
      const source = pendingConfirmSource || submitter || formElement
      const variant = source?.dataset.turboConfirmVariant || inferVariant(source, formElement)
      const copy = confirmCopyFor(source)
      let settled = false
      const dialog = buildDialog({
        message,
        title: copy.title,
        confirmLabel: copy.confirmLabel,
        cancelLabel: copy.cancelLabel,
        variant
      })

      const handleKeydown = (event) => {
        if (event.key !== "Escape") return

        close(false)
      }

      const cleanup = () => {
        document.removeEventListener("keydown", handleKeydown)
        document.body.classList.remove("overflow-hidden")
        dialog.overlay.remove()
        pendingConfirmSource = null
      }

      const close = (accepted) => {
        if (settled) return

        settled = true
        cleanup()
        resolve(accepted)
      }

      dialog.confirmButton.addEventListener("click", () => close(true), { once: true })
      dialog.cancelButton.addEventListener("click", () => close(false), { once: true })
      dialog.overlay.addEventListener("click", (event) => {
        if (event.target === dialog.overlay) close(false)
      })

      document.addEventListener("keydown", handleKeydown)
      document.body.classList.add("overflow-hidden")
      document.body.append(dialog.overlay)
      dialog.confirmButton.focus()
    })
}

function inferVariant(source, formElement) {
  if (source?.dataset.turboMethod === "delete") return "danger"
  if (source?.dataset.turboConfirmVariant === "danger") return "danger"
  if (formElement?.dataset.turboConfirmVariant === "danger") return "danger"
  if (formElement?.method?.toLowerCase() === "delete") return "danger"
  if (formElement?.querySelector('input[name="_method"][value="delete"]')) return "danger"

  return "primary"
}

function confirmCopyFor(source) {
  const dataset = document.documentElement.dataset

  return {
    title: source?.dataset.turboConfirmTitle ?? dataset.i18nConfirmTitle ?? dataset.i18n_confirm_title ?? "",
    confirmLabel:
      source?.dataset.turboConfirmConfirmLabel ??
      dataset.i18nConfirmConfirmLabel ??
      dataset.i18n_confirm_confirm_label ??
      "",
    cancelLabel:
      source?.dataset.turboConfirmCancelLabel ??
      dataset.i18nConfirmCancelLabel ??
      dataset.i18n_confirm_cancel_label ??
      ""
  }
}

function buildDialog({ title, message, confirmLabel, cancelLabel, variant }) {
  const overlay = document.createElement("div")
  overlay.className = "fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4 py-6 sm:p-6 md:p-8"
  overlay.innerHTML = `
    <div class="relative w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-xl outline-none">
      <div class="px-6 py-8 sm:px-8 sm:py-10">
        <div class="flex items-center gap-4">
          <div class="${iconContainerClasses(variant)}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="size-6" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.29 3.86l-7.5 13A2 2 0 0 0 4.5 20h15a2 2 0 0 0 1.71-3.14l-7.5-13a2 2 0 0 0-3.42 0Z" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="text-xl font-semibold leading-7 text-gray-900">${escapeHtml(title)}</h3>
          </div>
        </div>
        <div class="mt-4 pl-16 sm:pl-16">
          <p class="text-sm leading-6 text-gray-600">${escapeHtml(message)}</p>
        </div>
        <div class="mt-8 flex flex-col-reverse gap-3 pb-0 sm:flex-row sm:justify-end">
          <button type="button" class="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 md:w-auto">
            ${escapeHtml(cancelLabel)}
          </button>
          <button type="button" class="${confirmButtonClasses(variant)}">
            ${escapeHtml(confirmLabel)}
          </button>
        </div>
      </div>
    </div>
  `

  const buttons = overlay.querySelectorAll("button")

  return {
    overlay,
    cancelButton: buttons[0],
    confirmButton: buttons[1]
  }
}

function confirmButtonClasses(variant) {
  if (variant === "danger") {
    return "inline-flex w-full items-center justify-center rounded-lg border border-red-600 bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:border-red-700 hover:bg-red-700 md:w-auto"
  }

  return "inline-flex w-full items-center justify-center rounded-lg border border-primary-600 bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:border-primary-700 hover:bg-primary-700 md:w-auto"
}

function iconContainerClasses(variant) {
  if (variant === "danger") {
    return "flex size-12 flex-none items-center justify-center rounded-full bg-red-50 text-red-600"
  }

  return "flex size-12 flex-none items-center justify-center rounded-full bg-primary-50 text-primary-600"
}

function escapeHtml(value) {
  if (value == null) return ""

  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;")
}
